// Normalise an ecosystem feed into what the scene animates. Two feeds share
// one shape: the kit's GET /dev/ops/ecosystem ({ services, deploys,
// sessions, license, plane, activity }) and the control plane's
// GET /apps/:orgId/:appId/ecosystem (the `plane` object on its own).
// Levels: 'ok' | 'warn' | 'down' | 'off' (unknown / not applicable).

const worst = (...levels) => (levels.includes('down') ? 'down' : levels.includes('warn') ? 'warn' : levels.includes('ok') ? 'ok' : 'off');

export const ago = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

export function deriveEcosystem(data) {
  const d = data || {};
  const plane = d.plane && !d.plane.unreachable ? d.plane : (d.instance ? d : null);
  const planeReachable = !!plane;
  const license = d.license || null;
  const services = (d.services || []).map((s) => ({
    name: s.name, kind: s.kind || 'service',
    // A clean exit (init jobs, finished runners) is idle, not down.
    level: s.state !== 'running' ? (s.kind === 'runner' || s.exitCode === 0 ? 'off' : 'down') : s.health === 'unhealthy' ? 'down' : s.health === 'starting' ? 'warn' : 'ok',
    status: s.status || s.state,
  }));

  const inst = plane?.instance || {};
  const mon = plane?.monitor || null;
  const open = (plane?.incidents || []).filter((i) => i.status === 'open');
  const openIncident = open.length > 0;
  // Only probe/health incidents mean the instance is down; anomaly incidents
  // (traffic spike, 5xx burst, cascade…) are a warning on a reachable app.
  const outage = open.some((i) => i.kind !== 'anomaly');
  const monitorLevel = !mon ? 'off' : outage || mon.state === 'down' ? 'down' : openIncident || mon.state === 'degraded' || (mon.consecFails || 0) > 0 ? 'warn' : mon.state === 'up' ? 'ok' : 'off';

  const licenseLevel = !license || license.state === 'dormant' ? 'off' : license.state === 'valid' ? 'ok' : license.state === 'grace' ? 'warn' : license.state === 'pending' ? 'off' : 'down';
  const coreLevel = license?.state === 'blocked' ? 'down'
    : !planeReachable ? (services.length ? worst(...services.filter((s) => s.kind === 'service').map((s) => s.level)) : 'ok')
      : inst.status === 'live' ? worst('ok', monitorLevel === 'down' ? 'down' : 'ok') : inst.status === 'provisioning' ? 'warn' : inst.status ? 'down' : 'ok';

  const scan = plane?.scan || {};
  const counts = scan.counts || {};
  const securityLevel = scan.running ? 'warn' : (counts.critical || 0) > 0 ? 'down' : (counts.high || 0) > 0 ? 'warn' : scan.lastScanAt ? 'ok' : 'off';

  const twinSrc = plane?.twin;
  const activeDeploy = plane?.prod?.activeDeploy || (d.deploys || []).find((j) => j.status === 'running' || j.status === 'queued') || null;
  const twin = {
    exists: !!twinSrc?.exists,
    level: !twinSrc?.exists ? 'off' : twinSrc.status !== 'live' ? 'warn' : twinSrc.lastDeploy?.status === 'error' ? 'warn' : twinSrc.monitor?.state === 'down' ? 'down' : 'ok',
    prodLevel: plane?.prod?.lastDeploy?.status === 'error' ? 'warn' : 'ok',
    deployRunning: activeDeploy ? (activeDeploy.env === 'uat' ? 'uat' : 'prod') : null,
    lastDeploy: twinSrc?.lastDeploy || null,
  };

  const domains = (plane?.edge?.domains || []).map((x) => ({
    host: x.host, label: x.label, tlsDaysLeft: x.tlsDaysLeft ?? null,
    level: x.tlsDaysLeft != null && x.tlsDaysLeft < 7 ? 'down' : x.tlsDaysLeft != null && x.tlsDaysLeft < 14 ? 'warn' : 'ok',
  }));

  const updates = plane?.updates || {};
  const planeLevel = !planeReachable ? (license && license.state !== 'dormant' ? 'warn' : 'off') : licenseLevel === 'down' ? 'down' : licenseLevel === 'warn' ? 'warn' : 'ok';

  const events = (d.activity || plane?.activity || []).slice(0, 30);

  // One calm line under the scene. Empty = all quiet.
  const notes = [];
  if (license?.state === 'blocked') notes.push('license blocked');
  else if (license?.state === 'grace') notes.push('license in grace');
  if (!planeReachable && license && license.state !== 'dormant') notes.push('control plane unreachable');
  if (openIncident) { const n = open.length; notes.push(`${n} open incident${n === 1 ? '' : 's'}`); }
  else if (monitorLevel === 'warn') notes.push('monitor: probe failing');
  if (inst.status && inst.status !== 'live') notes.push(`instance ${inst.status}`);
  if (twin.deployRunning) notes.push(`deploy running → ${twin.deployRunning}`);
  if (scan.running) notes.push('security scan running');
  else if ((counts.critical || 0) + (counts.high || 0) > 0) notes.push(`scan: ${counts.critical || 0} critical · ${counts.high || 0} high`);
  const tlsMin = domains.reduce((m, x) => (x.tlsDaysLeft != null ? Math.min(m, x.tlsDaysLeft) : m), 99);
  if (tlsMin < 14) notes.push(`TLS ${tlsMin}d left`);
  if (updates.versionBehind) notes.push(`kit update available${updates.latestKitVersion ? ` · ${updates.latestKitVersion}` : ''}`);
  const down = services.filter((s) => s.kind === 'service' && s.level === 'down');
  if (down.length) notes.push(`${down.length} service${down.length > 1 ? 's' : ''} down: ${down.map((s) => s.name).slice(0, 3).join(', ')}`);
  const overall = worst(coreLevel, monitorLevel === 'off' ? 'ok' : monitorLevel, securityLevel === 'off' ? 'ok' : securityLevel, twin.level === 'off' ? 'ok' : twin.level, planeLevel === 'off' ? 'ok' : planeLevel, ...down.map(() => 'down'));

  return {
    overall,
    core: { level: coreLevel, name: inst.name || inst.slug || '', status: inst.status || (services.length ? 'running' : ''), kitRef: inst.kitRef || null, url: inst.url || null },
    // Where the viewer sits: 'prod' (default) or 'uat' when this feed was
    // requested by the UAT twin itself (the plane computes the pair from the
    // parent and stamps the viewer). Labels and the "you are here" marker
    // hang off this.
    viewer: inst.viewer || { role: 'prod', slug: inst.slug || '', name: inst.name || inst.slug || '', url: inst.url || null },
    services,
    monitor: { level: monitorLevel, lastCheckAt: mon?.lastCheckAt || 0, latencyMs: mon?.lastLatencyMs ?? null, incidentOpen: openIncident, tlsDaysLeft: mon?.tlsDaysLeft ?? null },
    security: { level: securityLevel, scanning: !!scan.running, counts, lastScanAt: scan.lastScanAt || 0 },
    twin,
    edge: domains,
    plane: { level: planeLevel, reachable: planeReachable, updates: !!updates.versionBehind, latestKitVersion: updates.latestKitVersion || null, license: license?.state || null },
    events,
    caption: notes.length ? notes.join(' · ') : (mon?.lastCheckAt ? `all quiet · monitor up · checked ${ago(mon.lastCheckAt)}` : planeReachable ? 'all quiet' : (services.length ? `${services.filter((s) => s.level === 'ok').length}/${services.length} services up` : 'waiting for data…')),
  };
}
